import { useCallback, useContext, useEffect, useMemo, useRef, useState } from "react";
import { AxiosError } from "axios";
import { ISocialInstitution } from "../../interfaces";
import { SortOrder } from "antd/es/table/interface";
import { EmitSuccessAlert } from "../../utils/EmitSuccessAlert";
import { EmitError } from "../../utils/EmitError";
import useAuth from "../../hooks/useAuth";
import { formatDateStringToDateAndTime, poeValorComPontosEmMil } from "../../utils/Conversores";
import { useGetColumnSearchPropsData } from "../../hooks/FilterTable/date";
import { useGetColumnSearchProps } from "../../hooks/FilterTable/stringClearFiltros";
import { formatCnpj, normalizeCnpjNumber } from "../../utils/Masks";
import SocialInstitutionService from "../../services/http/services/SocialInstitutionService";
import DashboardService from "../../services/http/services/DashboardService";
import { Space, Tag, Tooltip } from "antd";
import { TeamOutlined } from "@ant-design/icons";
import TableActionButton from "../../components/TableActionButton";
import { ThemeContext } from "../../contexts/ThemeContext";

interface ITableData extends ISocialInstitution {
    key: number | undefined;
}

export default function useSocialInstitution() {
    const { theme } = useContext(ThemeContext);
    const isDark = theme === 'dark';

    const [isLoading, setIsLoading] = useState<boolean>(true);
    const [isLoadingDelete, setIsLoadingDelete] = useState<boolean>(false);
    const [isDeleteModalVisible, setIsDeleteModalVisible] = useState<boolean>(false);
    const [tableData, setTableData] = useState<Array<ITableData>>([]);
    const [totalUsers, setTotalUsers] = useState<number>(0);
    const [selectedIds, setSelectedIds] = useState<Array<number>>([]);
    const [socialInstitutionBeingDeleted, setSocialInstitutionBeingDeleted] = useState<ITableData | null>(null);
    const refFuncaoLimparFiltrosString = useRef<any>(null);
    const [socialInstitutionSelected, setSocialInstitutionSelected] = useState<ISocialInstitution | undefined>();
    const [modalUsersVisible, setModalUsersVisible] = useState<boolean>(false);
    const [modalDistributionVisible, setModalDistributionVisible] = useState<boolean>(false);

    // Modal Form (Create / Edit)
    const [modalFormVisible, setModalFormVisible] = useState<boolean>(false);
    const [socialInstitutionToEdit, setSocialInstitutionToEdit] = useState<ISocialInstitution | null>(null);

    // Modal Row Actions (Options: View Users, Edit, Delete)
    const [modalActionsVisible, setModalActionsVisible] = useState<boolean>(false);
    const [socialInstitutionForActions, setSocialInstitutionForActions] = useState<ISocialInstitution | null>(null);

    const [pagination, setPagination] = useState({
        current: 1,
        pageSize: 10,
    });

    const { activeCity } = useAuth();

    const handleOpenCreateModal = useCallback(() => {
        setSocialInstitutionToEdit(null);
        setModalFormVisible(true);
    }, []);

    const handleOpenEditModal = useCallback((data: ISocialInstitution) => {
        setSocialInstitutionToEdit(data);
        setModalFormVisible(true);
    }, []);

    const handleCloseFormModal = useCallback(() => {
        setModalFormVisible(false);
        setSocialInstitutionToEdit(null);
    }, []);

    const handleOpenRowActions = useCallback((data: ISocialInstitution) => {
        setSocialInstitutionForActions(data);
        setModalActionsVisible(true);
    }, []);

    const handleCloseRowActions = useCallback(() => {
        setModalActionsVisible(false);
        setSocialInstitutionForActions(null);
    }, []);

    const handleDeleteSocialInstitution = useCallback((data: ISocialInstitution) => {
        setIsDeleteModalVisible(true);
        setSocialInstitutionBeingDeleted(tableData.find((element) => element.id === data.id) || null);
    }, [tableData]);

    const handleShowModalUsers = useCallback((institution?: ISocialInstitution) => {
        if (institution) {
            setSocialInstitutionSelected(institution);
        }
        setModalUsersVisible((prevState) => !prevState);
    }, []);

    const handleToggleModalDistribution = useCallback(() => {
        setModalDistributionVisible((prevState) => !prevState);
    }, []);

    const handlePaginationChange = useCallback((page: number, size: number) => {
        setPagination({ current: page, pageSize: size });
    }, []);

    const columns = [
        {
            title: 'Nome da Instituição',
            dataIndex: 'name',
            key: 'name',
            sorter: (a: ISocialInstitution, b: ISocialInstitution) => a.name.localeCompare(b.name),
            showSorterTooltip: false,
            ...useGetColumnSearchProps({ dataIndex: 'name', ref: refFuncaoLimparFiltrosString }),
            render: (name: string) => (
                <span style={{ fontWeight: 600, color: isDark ? '#f0f0f0' : '#1e293b' }}>
                    {name || '--'}
                </span>
            ),
        },
        {
            title: 'CNPJ',
            dataIndex: 'cnpj',
            key: 'cnpj',
            width: 170,
            ...useGetColumnSearchProps({ dataIndex: 'cnpj', ref: refFuncaoLimparFiltrosString, formatter: normalizeCnpjNumber }),
            render: (cnpj: string) => (
                <span style={{ fontFamily: 'monospace', color: isDark ? '#d9d9d9' : '#475569' }}>
                    {cnpj ? formatCnpj(cnpj) : '--'}
                </span>
            ),
        },
        {
            title: 'Status',
            dataIndex: 'active',
            key: 'active',
            width: 120,
            align: 'center' as const,
            sorter: (a: ISocialInstitution, b: ISocialInstitution) => Number(b.active) - Number(a.active),
            showSorterTooltip: false,
            filters: [
                { text: 'Ativo', value: true },
                { text: 'Inativo', value: false },
            ],
            onFilter: (value: any, record: ISocialInstitution) => record.active === value,
            render: (active: boolean) => (
                <Tag
                    color={active ? 'success' : 'error'}
                    style={{ borderRadius: 6, fontWeight: 600, margin: 0 }}
                >
                    {active ? 'Ativo' : 'Inativo'}
                </Tag>
            ),
        },
        {
            title: 'Data de Cadastro',
            dataIndex: 'created_at',
            key: 'created_at',
            width: 170,
            defaultSortOrder: 'descend' as keyof SortOrder,
            sorter: (a: ITableData, b: ITableData) =>
                new Date(a.created_at || '').getTime() - new Date(b.created_at || '').getTime(),
            ...useGetColumnSearchPropsData({ dataIndex: 'created_at' }),
            render: (criado: any) => (
                <span style={{ color: isDark ? '#8c8c8c' : '#64748b', fontSize: 13 }}>
                    {criado ? formatDateStringToDateAndTime(criado, true) : '--'}
                </span>
            ),
            showSorterTooltip: false,
        },
        {
            title: 'Apoiadores',
            dataIndex: 'total_users',
            key: 'total_users',
            width: 130,
            align: 'center' as const,
            sorter: (a: ISocialInstitution, b: ISocialInstitution) => (a.total_users ?? 0) - (b.total_users ?? 0),
            showSorterTooltip: false,
            render: (_: any, record: ISocialInstitution) => {
                const count = record.total_users ?? 0;
                return (
                    <Tooltip color="#eb2f96" title={`${count} munícipe(s) apoiam esta entidade`}>
                        <span
                            style={{
                                fontWeight: 600,
                                color: '#eb2f96',
                                fontSize: 14,
                                cursor: 'pointer',
                                whiteSpace: 'nowrap',
                            }}
                            onClick={(e) => {
                                e.stopPropagation();
                                setSocialInstitutionSelected(record);
                                handleShowModalUsers();
                            }}
                        >
                            {poeValorComPontosEmMil(count) ?? '0'}
                        </span>
                    </Tooltip>
                );
            },
        },
        {
            title: 'Ações',
            key: 'actions',
            align: 'center' as const,
            width: 130,
            fixed: 'right' as const,
            render: (_: any, record: ITableData) => (
                <Space size={4}>
                    <TableActionButton
                        action="custom"
                        icon={<TeamOutlined style={{ fontSize: 14, color: '#eb2f96' }} />}
                        tooltip="Ver apoiadores vinculados"
                        bgColor={isDark ? 'rgba(235, 47, 150, 0.15)' : '#fff0f6'}
                        onClick={(e) => {
                            e.stopPropagation();
                            setSocialInstitutionSelected(record);
                            handleShowModalUsers();
                        }}
                    />
                    <TableActionButton
                        action="edit"
                        tooltip="Editar instituição"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleOpenEditModal(record);
                        }}
                    />
                    <TableActionButton
                        action="delete"
                        tooltip="Excluir instituição"
                        onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteSocialInstitution(record);
                        }}
                    />
                </Space>
            ),
        },
    ];

    const setTableDataFromRequest = useCallback((data: Array<ISocialInstitution>) => {
        const newData = data.map((socialInstitution) => ({ ...socialInstitution, key: socialInstitution.id }));
        setTableData(newData);
    }, []);

    const loadSocialInstitutions = useCallback(async (signal?: AbortSignal) => {
        if (!activeCity?.id) {
            return;
        }

        try {
            setIsLoading(true);

            const [socialInstitutions, users] = await Promise.all([
                SocialInstitutionService.list(activeCity.id, signal),
                DashboardService.getTotalUsers({ countyId: activeCity.id, signal }).catch(() => ({ totalUsers: 0 })),
            ]);

            setTableDataFromRequest(socialInstitutions);
            setTotalUsers(users?.totalUsers ?? 0);
            setIsLoading(false);
        } catch (err) {
            if (err instanceof AxiosError && err.name === 'CanceledError') {
                return;
            }

            EmitError(err, true, 'socialInstitutions_asdiopasijep');
            setIsLoading(false);
        }
    }, [activeCity, setTableDataFromRequest]);

    useEffect(() => {
        const controller = new AbortController();
        loadSocialInstitutions(controller.signal);

        return () => {
            controller.abort();
        };
    }, [loadSocialInstitutions]);

    const handleSuccessForm = useCallback(() => {
        loadSocialInstitutions();
    }, [loadSocialInstitutions]);

    const stats = useMemo(() => {
        const totalLinkedUsers = tableData.reduce((acc, item) => acc + (item.total_users || 0), 0);
        const totalActive = tableData.filter((item) => item.active).length;
        const totalEntities = tableData.length;
        const totalUnlinkedUsers = Math.max(0, totalUsers - totalLinkedUsers);

        return {
            totalLinkedUsers,
            totalActive,
            totalEntities,
            totalUnlinkedUsers,
            totalUsers,
        };
    }, [tableData, totalUsers]);

    const onClickTableRow = useCallback((record: any) => {
        if (selectedIds.includes(record.id)) {
            setSelectedIds((prevState) => prevState.filter((id) => id !== record.id));
        } else {
            setSelectedIds((prevState) => [...prevState, record.id]);
        }
    }, [selectedIds]);

    const onSelectChange = useCallback((newSelectedRowKeys: Array<any>) => {
        setSelectedIds(newSelectedRowKeys);
    }, []);

    const rowSelection = useMemo(() => ({
        type: 'checkbox' as const,
        selectedRowKeys: selectedIds,
        onChange: onSelectChange,
    }), [selectedIds, onSelectChange]);

    const handleConfirmDeleteSocialInstitution = useCallback(async () => {
        try {
            setIsLoadingDelete(true);
            await SocialInstitutionService.delete(socialInstitutionBeingDeleted?.id!);
            EmitSuccessAlert('Instituição social deletada com sucesso!');
            setTableData((prevState) => prevState.filter((element) => element.id !== socialInstitutionBeingDeleted?.id));
            setIsDeleteModalVisible(false);
            setSocialInstitutionBeingDeleted(null);
            setSelectedIds([]);
            setIsLoadingDelete(false);
        } catch (err) {
            EmitError(err, true, 'social_institution_delete');
            setIsLoadingDelete(false);
        }
    }, [socialInstitutionBeingDeleted]);

    const handleCancelDeleteSocialInstitution = useCallback(async () => {
        setIsDeleteModalVisible(false);
        setSocialInstitutionBeingDeleted(null);
    }, []);

    return {
        isLoading,
        isDeleteModalVisible,
        isLoadingDelete,
        handleOpenCreateModal,
        handleOpenEditModal,
        handleCloseFormModal,
        handleSuccessForm,
        modalFormVisible,
        socialInstitutionToEdit,
        modalActionsVisible,
        socialInstitutionForActions,
        handleOpenRowActions,
        handleCloseRowActions,
        handleDeleteSocialInstitution,
        handleConfirmDeleteSocialInstitution,
        handleCancelDeleteSocialInstitution,
        columns,
        onClickTableRow,
        rowSelection,
        selectedIds,
        socialInstitutionBeingDeleted,
        tableData,
        handleShowModalUsers,
        modalUsersVisible,
        socialInstitutionSelected,
        stats,
        pagination,
        handlePaginationChange,
        modalDistributionVisible,
        setModalDistributionVisible,
        handleToggleModalDistribution,
    };
}

